import { Box, styled } from '@mui/material';
import { IPublicAndPrivateSettings } from '@pepperhq/location-sdk';

import { MuiCardForm } from 'lib/form/MuiCardForm';
import { FileInputFormField } from 'lib/form/fields/FileInputFormField';
import { KeyFileInputFormField } from 'lib/form/fields/KeyFileInputFormField';
import { SelectFormField } from 'lib/form/fields/SelectFormField';
import { SwitchFormField } from 'lib/form/fields/SwitchFormField';
import { TextFormField } from 'lib/form/fields/TextFormField';
import { SettingDisplay } from 'lib/SettingDisplay';
import { useMediaCheck } from 'lib/hooks/useMediaCheck';
import { Option } from 'lib/types';
import { SettingDisplaySwitch } from '../SettingDisplaySwitch';
import { SettingDisplayText } from '../SettingDisplayText';

const PREFIX = 'AppsSettingsPrefix';

const classes = {
    settingsCard: `${PREFIX}-settingsCard`,
    displayClassName: `${PREFIX}-displayClassName`
};

const StyledMuiCardForm = styled(MuiCardForm)(({ theme }) => ({
    [`&.${classes.settingsCard}`]: {
        marginBottom: theme.spacing(2)
    },
    [`& .${classes.displayClassName}`]: {
        padding: theme.spacing(2)
    }
}));

const filePickerAndroidAttributes = { type: 'file', accept: '.json' };
const filePickeriOSAttributes = { type: 'file', accept: '.plist' };
const filePickerFirebaseAttributes = { type: 'file', accept: '.json' };

enum EAppEnvironment {
    BETA = 'BETA',
    PRODUCTION = 'PRODUCTION'
}

const appEnvironmentOptions: Option[] = [
    {
        value: EAppEnvironment.BETA,
        label: 'Beta'
    },
    {
        value: EAppEnvironment.PRODUCTION,
        label: 'Production'
    }
];

type IProps = {
    settings: IPublicAndPrivateSettings;
    tenantId: string;
    onSubmit: (values: Record<string, any>) => Promise<boolean>;
};

export const AppsSettingsCard = ({ settings, tenantId, onSubmit }: IProps) => {
    const [androidFirebase, androidFirebaseLoading, _checkAndroidFirebase] = useMediaCheck(
        `${import.meta.env.MEDIA_SERVICE_URL}/tenants/${tenantId}/app_config/google-services.json`,
        'google-services.json'
    );
    const [iOSFirebase, iOSFirebaseLoading, _checkiOSFirebase] = useMediaCheck(
        `${import.meta.env.MEDIA_SERVICE_URL}/tenants/${tenantId}/app_config/GoogleService-Info.plist`,
        'GoogleService-Info.plist '
    );
    return (
        <StyledMuiCardForm
            title="Web, iOS & Android"
            initialValues={{
                displayName: settings?.app?.displayName,
                bundleIdentifier: settings?.app?.bundleIdentifier,
                bundleDisplayName: settings?.app?.bundleDisplayName,
                bundleName: settings?.app?.bundleName,
                appURLScheme: settings?.app?.appURLScheme,
                appStoreUrl: settings?.app?.appStoreUrl,
                appleKeyId: settings?.appleDeviceCheck?.keyId,
                appleTeamId: settings?.appleDeviceCheck?.teamId,
                appleKeyFile: settings?.appleDeviceCheck?.keyAsBase64,
                googleMapsApiKey: settings?.app?.googleMapsApiKey,
                playStoreUrl: settings?.app?.playStoreUrl,
                domainName: settings?.web?.domainName,
                environment: settings?.app?.environment,
                androidFirebase: undefined,
                iOSFirebase: undefined,
                firebaseCredentialsFile: settings?.firebase?.credentialsAsBase64,
                smartlookEnabled: settings?.smartlookEnabled ?? false
            }}
            onSubmit={onSubmit}
            childrenWhileDisplaying={
                <Box display="flex" flexDirection="column">
                    <SettingDisplayText
                        title="App Environment"
                        buildtimeAndroid
                        buildtimeIOS
                        text={settings?.app?.environment ?? <>&mdash;</>}
                    />
                    <SettingDisplayText title="Web App Domain" text={settings?.web?.domainName} />
                    <SettingDisplayText
                        title="App Display Name"
                        buildtimeAndroid
                        buildtimeIOS
                        text={settings?.app?.displayName}
                    />
                    <SettingDisplayText
                        title="Bundle Identifier"
                        buildtimeAndroid
                        buildtimeIOS
                        text={settings?.app?.bundleIdentifier}
                    />
                    <SettingDisplayText
                        title="Bundle Display Name"
                        buildtimeAndroid
                        buildtimeIOS
                        text={settings?.app?.bundleDisplayName}
                    />
                    <SettingDisplayText
                        title="Bundle Name"
                        buildtimeAndroid
                        buildtimeIOS
                        text={settings?.app?.bundleName}
                    />
                    <SettingDisplayText
                        title="App URL Scheme"
                        buildtimeAndroid
                        buildtimeIOS
                        text={settings?.app?.appURLScheme}
                    />
                    <SettingDisplayText title="App Store URL" text={settings?.app?.appStoreUrl} />
                    <SettingDisplayText
                        title="Apple API Auth Key ID"
                        text={settings?.appleDeviceCheck?.keyId}
                    />
                    <SettingDisplayText
                        title="Apple Team ID"
                        buildtimeIOS
                        text={settings?.appleDeviceCheck?.teamId}
                    />

                    <SettingDisplayText
                        title="Apple Key File"
                        text={settings?.appleDeviceCheck?.keyAsBase64}
                        crop
                    />
                    <SettingDisplayText
                        title="Google Maps API Key"
                        buildtimeAndroid
                        text={settings?.app?.googleMapsApiKey}
                    />
                    <SettingDisplayText title="Play Store URL" text={settings?.app?.playStoreUrl} />
                    <SettingDisplayText
                        title="Android Firebase Config File"
                        buildtimeAndroid
                        text={
                            androidFirebase
                                ? androidFirebase
                                : 'Click Edit to upload Android Firebase Config File'
                        }
                    />
                    <SettingDisplayText
                        title="IOS Firebase Config File"
                        buildtimeIOS
                        text={iOSFirebase ? iOSFirebase : 'Click Edit to upload IOS Firebase Config File'}
                    />
                    <SettingDisplayText
                        title="Firebase Credentials File"
                        text={settings?.firebase?.credentialsAsBase64}
                        crop
                    />
                    <SettingDisplaySwitch title="Smartlook Enabled" checked={settings?.smartlookEnabled} />
                </Box>
            }
            childrenWhileEditing={
                <Box display="flex" flexDirection="column">
                    <SettingDisplay label="App Environment" buildtimeAndroid buildtimeIOS>
                        <SelectFormField options={appEnvironmentOptions} name="environment" />
                    </SettingDisplay>
                    <SettingDisplay label="Web App Domain">
                        <TextFormField name="domainName" />
                    </SettingDisplay>
                    <SettingDisplay label="App Display Name" buildtimeAndroid buildtimeIOS>
                        <TextFormField name="displayName" />
                    </SettingDisplay>
                    <SettingDisplay label="Bundle Identifier" buildtimeAndroid buildtimeIOS>
                        <TextFormField name="bundleIdentifier" />
                    </SettingDisplay>
                    <SettingDisplay label="Bundle Display Name" buildtimeAndroid buildtimeIOS>
                        <TextFormField name="bundleDisplayName" />
                    </SettingDisplay>
                    <SettingDisplay label="Bundle Name" buildtimeAndroid buildtimeIOS>
                        <TextFormField name="bundleName" />
                    </SettingDisplay>
                    <SettingDisplay label="App URL Scheme" buildtimeAndroid buildtimeIOS>
                        <TextFormField name="appURLScheme" />
                    </SettingDisplay>
                    <SettingDisplay label="App Store URL">
                        <TextFormField name="appStoreUrl" />
                    </SettingDisplay>
                    <SettingDisplay label="Apple API Auth Key ID">
                        <TextFormField name="appleKeyId" />
                    </SettingDisplay>
                    <SettingDisplay label="Apple Team ID" buildtimeIOS>
                        <TextFormField name="appleTeamId" />
                    </SettingDisplay>
                    <SettingDisplay label="Apple Key File">
                        <KeyFileInputFormField name="appleKeyFile" />
                    </SettingDisplay>
                    <SettingDisplay label="Google Maps API Key" buildtimeAndroid>
                        <TextFormField name="googleMapsApiKey" />
                    </SettingDisplay>
                    <SettingDisplay label="Play Store URL">
                        <TextFormField name="playStoreUrl" />
                    </SettingDisplay>
                    <SettingDisplay label="Android Firebase Config File" buildtimeAndroid>
                        <FileInputFormField
                            name="androidFirebase"
                            attributes={filePickerAndroidAttributes}
                            path={androidFirebase}
                            isLoading={androidFirebaseLoading}
                        />
                    </SettingDisplay>
                    <SettingDisplay label="IOS Firebase Config File" buildtimeIOS>
                        <FileInputFormField
                            name="iOSFirebase"
                            attributes={filePickeriOSAttributes}
                            path={iOSFirebase}
                            isLoading={iOSFirebaseLoading}
                        />
                    </SettingDisplay>
                    <SettingDisplay label="Firebase Credentials File">
                        <KeyFileInputFormField
                            name="firebaseCredentialsFile"
                            attributes={filePickerFirebaseAttributes}
                        />
                    </SettingDisplay>
                    <SettingDisplay label="Smartlook Enabled">
                        <SwitchFormField name="smartlookEnabled" />
                    </SettingDisplay>
                </Box>
            }
            className={classes.settingsCard}
            boxClassName={classes.displayClassName}
        />
    );
};
